/**
 * HTMLBook Schema for ProseMirror.
 */
import { MarkSpec, NodeSpec, Schema } from 'prosemirror-model';
import { marks as basicMarks, nodes as basicNodes } from 'prosemirror-schema-basic';
import { tableNodes } from 'prosemirror-tables';
import { listItem } from 'prosemirror-schema-list';

export const createId = (): string =>
  "abcdefghijklmnopqrstuvwxyz".charAt(Math.floor(Math.random() * 26)) +
  Math.random()
    .toString(36)
    .substr(2, 12);
let htmlBookSchema;
let documentTitleSchema;
let inlineSchema: Schema;
/** A schema for free form cover pages */
let freeSchema;
let titleOnlySchema;
let documentSchema;
let allNodes;

try {
  // all ids must start with a letter (for CSS styling to be able to target IDs)
  const idAttr = { default: null }; // ids are generated by the id plugin
  const readID = dom => ({
    id: dom.getAttribute("id") || dom.getAttribute("data-id")
  });

  function extend(obj, fields) {
    return Object.assign({}, obj, fields);
  }

  /** Root note for parts in part.directive */
  const component = {
    attrs: {
      type: { default: "chapter" },
      lang: { default: undefined },
      role: { default: undefined },
      schema: { default: undefined },
      pageBreak: { default: undefined },
      placement: { default: undefined },
      numbering: { default: undefined }
    },
    content: "heading block*",
    marks: "_"
  };

  const placeHolder: NodeSpec = {
    content: "",
    selectable: true,
    inline: false,
    draggable: true,
    isolating: false,
    atom: true,
    group: "block",
    attrs: {
      id: idAttr,
      type: { default: "logo" },
      label: { default: "Logo" }
    },
    toDOM(node) {
      return ["div", { id: node.attrs.id, type: node.attrs.type, label: node.attrs.label }];
    }
  };

  const bulletList: NodeSpec = {
    group: "block",
    content: "list_item+",
    attrs: { "list-style-type": { default: undefined } },
    parseDOM: [
      {
        tag: "ul",
        getAttrs(dom: HTMLElement) {
          return {
            "list-style-type": dom.getAttribute("data-list-style-type")
          };
        }
      }
    ],
    toDOM(node) {
      return [
        "ul",
        { "data-list-style-type": node.attrs?.["list-style-type"] },
        0
      ];
    }
  };

  const orderedList: NodeSpec = {
    group: "block",
    content: "list_item+",
    attrs: { order: { default: 1 }, "list-style-type": { default: undefined } },
    parseDOM: [
      {
        tag: "ol",
        getAttrs(dom: HTMLElement) {
          return {
            "list-style-type": dom.getAttribute("data-list-style-type"),
            order: dom.hasAttribute("start") ? +dom.getAttribute("start")! : 1
          };
        }
      }
    ],
    toDOM(node) {
      return node.attrs.order == 1
        ? ["ol", { "data-list-style-type": node.attrs?.["list-style-type"] }, 0]
        : [
            "ol",
            {
              start: node.attrs.order,
              "data-list-style-type": node.attrs?.["list-style-type"]
            },
            0
          ];
    }
  };

  const header = {
    content: "heading subtitle?",
    marks: "sub sup em strong bdi tags",
    toDOM() {
      return ["header", 0];
    },
    parseDOM: [{ tag: "header" }]
  };

  const heading = {
    content: "text*",
    group: "block",
    marks: "sub sup em strong bdi tags",
    defining: false,
    selectable: false,
    attrs: { id: idAttr, level: { default: 1 }, data: { default: undefined }, },
    toDOM(node) {
      return [
        "h" + node.attrs.level,
        {
          "data-id": node.attrs.id,
          id: node.attrs.id,
          "outline-level": node.attrs.level
        },
        0
      ];
    },
    parseDOM: [
      { tag: "h1", getAttrs: dom => ({ ...readID(dom), level: 1 }) },
      { tag: "h2", getAttrs: dom => ({ ...readID(dom), level: 2 }) },
      { tag: "h3", getAttrs: dom => ({ ...readID(dom), level: 3 }) },
      { tag: "h4", getAttrs: dom => ({ ...readID(dom), level: 4 }) },
      { tag: "h5", getAttrs: dom => ({ ...readID(dom), level: 5 }) },
      { tag: "h6", getAttrs: dom => ({ ...readID(dom), level: 6 }) }
    ]
  };

  const subtitle = {
    content: "inline*",
    group: "block",
    marks: "_",
    toDOM() {
      return ["h2", { "data-type": "subtitle" }, 0];
    },
    parseDOM: [{ tag: "h2[data-type=subtitle]" }]
  };

  const paragraph: NodeSpec = {
    content: "inline*",
    marks: "_",
    group: "block",
    attrs: {
      id: idAttr,
      "text-align": { default: undefined },
      "text-direction": { default: undefined },
      class: { default: undefined },
      lang: { default: undefined }
    },
    toDOM(node) {
      return [
        "p",
        {
          "data-id": node.attrs.id,
          id: node.attrs.id,
          lang: node.attrs.lang,
          "data-class": node.attrs.class,
          "text-direction": node.attrs["text-direction"]
            ? node.attrs["text-direction"]
            : null,
          "text-align": node.attrs["text-align"]
            ? node.attrs["text-align"]
            : null,
          style: `text-align: ${node.attrs["text-align"]}`
        },
        0
      ];
    },
    parseDOM: [{ tag: "p", getAttrs: readID }]
  };

  const blockquote = {
    content: "block+",
    group: "block",
    defining: true,
    marks: "_",
    attrs: { id: idAttr, lang: { default: undefined } },
    parseDOM: [
      {
        tag: "blockquote"
      }
    ],
    toDOM(node) {
      return [
        "blockquote",
        {
          "data-id": node.attrs.id,
          id: node.attrs.id,
          lang: node.attrs.lang,
          "text-direction": { default: undefined }
        },
        0
      ];
    }
  };

  const note = {
    content: "block+",
    group: "block",
    defining: true,
    attrs: { id: idAttr, type: { default: "note" } },
    parseDOM: [
      {
        tag: 'blockquote[data-type="note"]',
        getAttrs: dom => ({ type: dom.getAttribute("type") })
      }
    ],
    toDOM(node) {
      return [
        "blockquote",
        {
          "data-type": node.attrs.type ?? "note",
          "data-id": node.attrs.id,
          id: node.attrs.id
        },
        0
      ];
    }
  };

  const poetry = {
    content: "block+",
    group: "block",
    defining: true,
    selectable: true,
    whitespace: "pre",
    attrs: {
      id: idAttr,
      type: { default: "poetry" },
      lang: { default: undefined }
    },
    parseDOM: [{ tag: 'blockquote[data-type="poetry"]' }],
    toDOM(node) {
      return [
        "blockquote",
        {
          "data-type": node.attrs.type ?? "poetry",
          "data-id": node.attrs.id,
          id: node.attrs.id,
          lang: node.attrs.lang
        },
        0
      ];
    }
  };

  const footnote = {
    group: "inline",
    content: "inline*",
    inline: true,
    marks: "_",
    // setting draggable to false since true will expose odd behavior in safari @see https://github.com/ProseMirror/website/issues/86
    draggable: false,
    // This makes the view treat the node as a leaf, even though it
    // technically has content
    atom: true,
    attrs: {
      id: idAttr,
      type: { default: "footnote" },
      lang: { default: undefined }
    },
    toDOM(node) {
      return [
        "span",
        {
          "data-type": "footnote",
          "data-id": node.attrs.id,
          id: node.attrs.id,
          lang: node.attrs.lang
        },
        0
      ];
    },
    parseDOM: [{ tag: "span[data-type='footnote']", getAttrs: readID }]
  };

  const figure: any = {
    content: "table? caption",
    group: "block",
    marks: "tags",
    draggable: true,
    selectable: false,
    isolating: true,
    attrs: {
      id: idAttr,
      src: { default: "" },
      alt: { default: "" },
      decorative: { default: undefined },
      type: { default: "figure" },
      width: { default: undefined },
      height: { default: undefined },
      environment: { default: undefined },
      orientation: { default: "portrait" },
      "scale-width": { default: 1 },
      "float-placement": { default: undefined },
      "float-reference": { default: undefined },
      "float-defer-page": { default: undefined },
      "float-modifier": { default: undefined }
    },
    toDOM(node) {
      return [
        "figure",
        {
          "data-id": node.attrs["id"],
          id: node.attrs.id,
          width: node.attrs.width,
          height: node.attrs.height,
          "data-type": node.attrs["type"],
          "data-alt": node.attrs["alt"],
          "data-src": node.attrs["src"],
          "data-scale-width": node.attrs["scale-width"],
          "data-orientation": node.attrs["orientation"]
        },
        0
      ];
    },
    parseDOM: [
      {
        tag: "figure",
        getAttrs: dom => {
          return {
            id: dom.getAttribute("data-id"),
            src: dom.getAttribute("data-src"),
            alt: dom.getAttribute("data-alt"),
            width: dom.getAttribute("width"),
            height: dom.getAttribute("height"),
            orientation: dom.getAttribute("data-orientation"),
            type: dom.getAttribute("data-type")
          };
        }
      }
    ]
  };

  const label = {
    content: "text*",
    marks: "_",
    toDOM() {
      return ["label", 0];
    },
    parseDOM: [{ tag: "label" }]
  };

  const caption = {
    content: "label? block*",
    marks: "_",
    toDOM() {
      return ["figcaption", 0];
    },
    parseDOM: [{ tag: "figcaption" }]
  };

  const code_block = {
    content: "text*",
    marks: "",
    code: true,
    defining: true,
    draggable: true,
    selectable: true,
    group: "block",
    attrs: { id: idAttr, language: { default: "text" } },
    parseDOM: [
      {
        tag: "pre",
        preserveWhitespace: "full",
        getAttrs: dom => ({
          language: dom.getAttribute("data-language") || "text",
          ...readID(dom)
        })
      }
    ],
    toDOM(node) {
      return [
        "pre",
        {
          "data-language": node.attrs.language,
          "data-id": node.attrs.id,
          id: node.attrs.id
        },
        0
      ];
    }
  };

  const pageBreak = {
    group: "block",
    selectable: false,
    draggable: true,
    toDOM() {
      return ["div", { "data-type": "page-break" }];
    },
    parseDOM: [
      {
        tag: 'div[data-type="page-break"]',
        getAttrs: dom => ({ "data-type": dom.getAttribute("data-type") })
      }
    ]
  };

  const math = {
    group: "inline",
    content: "text*",
    inline: true,
    code: true,
    draggable: true,
    defining: true,
    atom: true,
    attrs: { id: idAttr, tex: { default: "" }, style: { default: "inline" } },
    toDOM(node) {
      return [
        "math",
        {
          "data-id": node.attrs.id,
          id: node.attrs.id,
          "data-tex": node.attrs.tex,
          "data-style": node.attrs.style
        },
        0
      ];
    },
    parseDOM: [
      {
        tag: "math",
        getAttrs: dom => ({
          id: dom.getAttribute("data-id"),
          tex: dom.getAttribute("data-tex"),
          style: dom.getAttribute("data-style")
        }),
        preserveWhitespace: "full"
      }
    ]
  };

  const citation = {
    attrs: {
      source: {},
      style: { default: "apa" },
      id: idAttr,
      lang: { default: undefined }
    },
    inline: true,
    content: "inline*",
    draggable: true,
    selectable: true,
    isolating: false,
    atom: true,
    group: "inline",
    // If you are not using a nodeview, make sure your editor instance supplies a citationRenderer,
    // otherwise all citations RENDERER will be rendered as NO RENDERER SUPPLIED.
    toDOM(node) {
      return [
        "cite",
        {
          "data-source": node.attrs.source,
          "data-style": node.attrs.style,
          lang: node.attrs.lang
        },
        "NO RENDERER SUPPLIED"
      ];
    },
    parseDOM: [
      {
        tag: "cite[data-source]",
        getAttrs(dom) {
          return {
            source: dom.getAttribute("data-source"),
            style: dom.getAttribute("data-style")
          };
        }
      }
    ]
  };

  /**
   * @deprecated use the anchor mark
   */
  const hyperlink = {
    inline: true,
    attrs: {},
    group: "inline",
    content: "inline*",
    toDOM(node) {
      return ["span", 0];
    }
  };

  const image: NodeSpec = {
    inline: true,
    attrs: {
      src: { default: undefined },
      alt: { default: undefined },
      title: { default: undefined },
      width: { default: undefined },
      height: { default: undefined },
      decorative: { default: undefined },
      id: idAttr
    },
    group: "inline",
    draggable: true,
    selectable: true,
    parseDOM: [
      {
        tag: "img[src]",
        getAttrs(dom) {
          return {
            src: dom.getAttribute("src"),
            title: dom.getAttribute("title"),
            alt: dom.getAttribute("alt"),
            width: dom.getAttribute("width"),
            height: dom.getAttribute("height"),
            decorative:
              dom.getAttribute("role") === "presentation" ? true : undefined,
            ...readID(dom)
          };
        }
      }
    ],
    toDOM(node) {
      let { src, alt, title, id, width, height, decorative } = node.attrs;

      const attrs: Record<string, string | number | undefined> = {
        src,
        title,
        id,
        width,
        height
      };

      if (alt) {
        attrs["alt"] = alt;
      }

      if (decorative === true) {
        attrs["role"] = "presentation";
        attrs["alt"] = "";
      }

      return ["img", attrs];
    }
  };

  const link: NodeSpec = {
    attrs: { id: idAttr, type: {}, href: {} },
    inline: true,
    content: 'text*',
    group: 'inline',
    selectable: false,
    draggable: true,
    toDOM(node) {
      return [
        'a',
        {
          id: node.attrs.id,
          'data-type': node.attrs.type,
          'href': node.attrs.href,
          'reference-format': node.attrs['reference-format'],
        },
        0,
      ];
    },
    parseDOM: [
      {
        tag: 'a[href][data-type=xref]',
        // @ts-ignore
        getAttrs(dom: HTMLElement) {
          return {
            id: dom.getAttribute('id'),
            type: dom.getAttribute('data-type'),
            href: dom.getAttribute('href'),
            'reference-format': dom.getAttribute('reference-format'),
          };
        },
      },
    ],
  };

  const horizontal_rule = {
    group: "block",
    parseDOM: [{ tag: "hr" }],
    toDOM() {
      return ["hr"];
    }
  };

  // Marks
  const superscriptMark: MarkSpec = {
    toDOM() {
      return ["sup"];
    },
    parseDOM: [{ tag: "sup" }]
  };

  const subscriptMark: MarkSpec = {
    toDOM() {
      return ["sub"];
    },
    parseDOM: [{ tag: "sub" }]
  };

  const bdiMark: MarkSpec = {
    toDOM() {
      return ["bdi"];
    },
    parseDOM: [{ tag: "bdi" }]
  };

  const tagsMark: MarkSpec = {
    attrs: { tags: { default: [] } },
    inclusives: false,
    toDOM(mark) {
      return ["span", { "data-tags": JSON.stringify(mark.attrs.tags) }];
    },
    parseDOM: [{ tag: "span[data-tags]" }]
  };

  /** The primary way to create a hyperlink (as opposed to a crossreference) */
  const anchorMark: MarkSpec = {
    attrs: {
      href: { default: null },
      title: { default: null },
      id: { default: null }
    },
    content: "inline*",
    inline: true,
    selectable: false,
    defining: true,
    group: "inline",
    toDOM(node) {
      return [
        "a",
        {
          href: node.attrs.href,
          title: node.attrs.title,
          "data-id": node.attrs.id
        },
        0
      ];
    },
    parseDOM: [
      {
        tag: "a[href]:not([data-type=xref])",
        getAttrs(dom: HTMLElement) {
          return {
            href: dom.getAttribute("href"),
            title: dom.getAttribute("title"),
            id: dom.getAttribute("data-id")
          };
        }
      }
    ]
  };

  const marks: any = {
    anchor: anchorMark,
    bdi: bdiMark, // we want this to be first(ish)
    em: basicMarks.em,
    strong: basicMarks.strong,
    sup: superscriptMark,
    sub: subscriptMark,
    code: basicMarks.code,
    tags: tagsMark
  };

  inlineSchema = new Schema({
    nodes: {
      text: basicNodes.text,
      doc: footnote,
      citation,
      math,
      link,
      hard_break: basicNodes.hard_break
    } as any,
    marks
  });

  titleOnlySchema = new Schema({
    nodes: {
      doc: extend(component, { content: "header", marks: "_" }),
      header,
      heading,
      subtitle,
      text: basicNodes.text
    },
    marks: {
      bdi: bdiMark, // we want this to be first
      sup: superscriptMark,
      sub: subscriptMark,
      em: basicMarks.em,
      strong: basicMarks.strong,
      tags: tagsMark
    }
  } as any);

  const tableNodeList: any = tableNodes({
    tableGroup: "block",
    cellContent:
      "(paragraph | ordered_list | bullet_list | figure | blockquote)*",
    cellAttributes: {
      background: {
        default: null,
        getFromDOM(dom: HTMLElement) {
          return dom.style.backgroundColor || null;
        },
        setDOMAttr(value, attrs) {
          if (value)
            attrs.style = (attrs.style || "") + `background-color: ${value};`;
        }
      }
    }
  });

  tableNodeList.table.attrs = {
    ...(tableNodeList.table.attrs || {}),
    id: idAttr
  };

  tableNodeList.table_row.attrs = {
    ...(tableNodeList.table_row.attrs || {}),
    breakAfter: { default: null },
    id: idAttr
  };

  tableNodeList.table_row.toDOM = node => {
    return ["tr", { "data-break-after": node.attrs.breakAfter }, 0];
  };
  tableNodeList.table_row.parseDOM = [
    {
      tag: "tr",
      getAttrs: (dom: HTMLElement) => ({
        ...readID(dom),
        breakAfter: dom.getAttribute("data-break-after")
      })
    }
  ];
  tableNodeList.table.marks = "_";
  tableNodeList.table_cell.marks = "_";
  tableNodeList.table_header.marks = "_";

  const baseSchema = new Schema({
    nodes: {
      doc: component,
      header,
      paragraph, // should be first so it's the default node
      image,
      horizontal_rule,
      list_item: { ...listItem, content: "block+", marks: "_" },
      ordered_list: orderedList,
      bullet_list: bulletList,
      // order of note and poetry before blockquote is important
      note,
      poetry,
      blockquote,
      //
      heading,
      subtitle,
      pageBreak,
      // sidebar,
      // admonition,
      // iframe,
      label,
      caption,
      // mention,
      // example,
      code_block,
      hyperlink,
      math,
      text: basicNodes.text,
      hard_break: basicNodes.hard_break,
      citation,
      link,
      footnote
    } as any,
    marks
  });

  const part = {
    // FIXME # make headings optional in exports/toc generation
    content: "heading block*",
    marks: "_",
    group: "structural",
    selectable: true,
    draggable: false,
    defining: false,
    isolating: false,
    attrs: {
      id: idAttr,
      type: { default: "chapter" },
      locale: { default: undefined },
      numbering: { default: undefined },
      placement: { default: undefined },
      role: { default: undefined },
      "text-direction": { default: undefined },
      skipToc: { default: false },
      data: { default: undefined },
    },
    toDOM(node) {
      const attrs = {
        id: node.attrs.id,
        "data-id": node.attrs.id,
        "data-type": node.attrs.type,
        "data-locale": node.attrs.language,
        "data-numbering": node.attrs.numbering,
        "data-placement": node.attrs.placement,
        "data-role": node.attrs.role,
        "text-direction": node.attrs["text-direction"]
          ? node.attrs["text-direction"]
          : null,
        "data-class": node.attrs.class
      };

      if (node.attrs.data) {
        attrs["data-extra"] = JSON.stringify(node.attrs.data);
      }

      return ["section", attrs, 0];
    },
    parseDOM: [
      {
        tag: 'section[data-type="part"]',
        getAttrs: dom => {
          const baseAttrs = {
            ...readID(dom),
            type: dom.getAttribute("data-type"),
            language: dom.getAttribute("data-language"),
            numbering: dom.getAttribute("data-numbering"),
            placement: dom.getAttribute("data-placement"),
            role: dom.getAttribute("data-role"),
            class: dom.getAttribute("data-class")
          };

          const extraData = dom.getAttribute("data-extra");
          if (extraData) {
            baseAttrs["data"] = JSON.parse(extraData);
          }

          return baseAttrs;
        }
      }
    ]
  };

  let schemaNodes = baseSchema.spec.nodes;

  allNodes = schemaNodes.append(tableNodeList).append({ figure });

  htmlBookSchema = new Schema({
    nodes: allNodes.update("doc", part),
    marks
  });

  documentTitleSchema = new Schema({
    nodes: {
      doc: header,
      heading,
      subtitle,
      text: basicNodes.text
    },
    marks: {
      bdi: bdiMark, // we want this to be first
      sup: superscriptMark,
      sub: subscriptMark,
      em: basicMarks.em,
      strong: basicMarks.strong,
      tags: tagsMark
    }
  } as any);

  freeSchema = new Schema({
    nodes: {
      doc: {
        content: "block*",
        toDOM() {
          return ["div", 0];
        },
        parseDOM: [{ tag: "div" }],
        marks: "_"
      },
      paragraph, // should be first so it's the default node
      image,
      horizontal_rule,
      list_item: { ...listItem, content: "block+" },
      ordered_list: orderedList,
      bullet_list: bulletList,
      heading,
      // order is important so poetry will be matched before blockquote
      note,
      poetry,
      blockquote,
      label,
      caption,
      ...tableNodeList,
      text: basicNodes.text,
      placeHolder,
      figure,
      hard_break: basicNodes.hard_break,
      subtitle,
      pageBreak,
      // sidebar,
      // admonition,
      // iframe,
      // mention,
      // example,
      code_block,
      hyperlink,
      math,
      citation,
      link,
      footnote
    },
    marks: {
      anchor: anchorMark,
      bdi: bdiMark, // we want this to be first(ish)
      sup: superscriptMark,
      sub: subscriptMark,
      em: basicMarks.em,
      strong: basicMarks.strong,
      tags: tagsMark
    }
  });

  const mergedDocumentNodes = allNodes
    .append({ subtitle, header })
    .append({ placeHolder })
    .append({
      chapter: {
        ...component,
        content: "heading? block*",
        attrs: {
          id: idAttr,
          type: { default: "chapter" },
          role: { default: null }
        },
        toDOM(node) {
          return [
            "section",
            {
              "data-id": node.attrs.id,
              id: node.attrs.id,
              "data-type": node.attrs.type,
              "data-role": node.attrs.role
            },
            0
          ];
        },
        parseDOM: [{ tag: "section" }]
      }
    })
    // FIXME #1106: Update 'doc' to hold data attr for alt text
    .update("doc", {
      content: "header? chapter*",
      toDOM() {
        return ["article", 0];
      },
      parseDOM: [{ tag: "article" }]
    });

  documentSchema = new Schema({
    nodes: mergedDocumentNodes,
    marks
  });

} catch (e: any) {
  console.error('Could not create schemas', e.message);
  throw e;
}

const schemas = {
  inline: inlineSchema,
  chapter: htmlBookSchema,
  title: documentTitleSchema,
  document: documentSchema,
  free: freeSchema
};

export {
  allNodes,
  documentTitleSchema,
  freeSchema,
  htmlBookSchema,
  inlineSchema,
  schemas,
  titleOnlySchema
};
